import { Trans } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  apiGetClinicalStudies,
  apiGetClinicalStudiesStudyAcronymOverviewPatientPatientId,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import { Spinner } from "../../../components/ui-kit/spinner";
import { assertStudyAcronym } from "../../../utils/clinical-studies";
import { useFetch } from "../../../utils/use-fetch";
import { compareDateStr, dateTimeOrNull } from "../../../utils/utils";
import { prettyVisitType } from "../../clinical-studies/utils";
import { EnrollDialog } from "./enroll-dialog";
import { PatientOverviewTable } from "./patient-overview-table";

export const EnrollmentDetailsRoute = observer(
  function EnrollmentDetailsRoute(props: { app: AppModel }) {
    const patient = props.app.requirePatient();
    const params = useParams<{ study: string }>();

    const study = assertStudyAcronym(params.study);
    const navigate = useNavigate();

    const [data, setData] = useState<any>();
    const [overviewData, setOverviewData] = useState<any>();
    const [showEditDialog, setShowEditDialog] = useState(false);

    const { loading, refetch } = useFetch(async () => {
      const response = await apiGetClinicalStudies({
        query: { patient_ids: [patient.patientId] },
      });
      const enrollment = response.data.study_enrollments.find(
        (item) => item.study_acronym === study,
      );
      setData(enrollment);
    }, [patient.patientId, study]);

    const { loading: overviewLoading, refetch: refetchOverview } =
      useFetch(async () => {
        const response =
          await apiGetClinicalStudiesStudyAcronymOverviewPatientPatientId({
            path: {
              study_acronym: study,
              patient_id: patient.patientId,
            },
          });
        setOverviewData(response.data);
      }, [patient.patientId, study]);

    const refetchAll = async () => {
      await Promise.all([refetch(), refetchOverview()]);
      setShowEditDialog(false);
    };

    return (
      <div>
        {loading ? (
          <Spinner />
        ) : data ? (
          <div>
            <Button onClick={() => setShowEditDialog(true)}>
              <Trans>Edit</Trans>
            </Button>
            {showEditDialog && (
              <EnrollDialog
                app={props.app}
                editStudy={study}
                onClose={() => setShowEditDialog(false)}
                onSaved={() => {
                  void refetchAll();
                }}
                onDeleted={() => {
                  setShowEditDialog(false);
                  void navigate("..");
                }}
              />
            )}

            <h2 className="mt-10 mb-2 text-base font-bold">
              <Trans>Visits</Trans>
            </h2>

            <ul className="list-disc pl-5 mt-4">
              {data.study_visits
                .slice()
                .sort((a: any, b: any) =>
                  compareDateStr(a.visit_datetime, b.visit_datetime),
                )
                .map((visit: any) => (
                  <li key={visit.uuid}>
                    <Link to={visit.visit_type}>
                      {prettyVisitType(visit.visit_type)}
                    </Link>{" "}
                    ({dateTimeOrNull(visit.visit_datetime, false)})
                  </li>
                ))}
            </ul>

            <h2 className="mt-10 mb-2 text-base font-bold">
              <Trans>Data overview</Trans>
            </h2>

            {overviewLoading ? (
              <Spinner />
            ) : (
              overviewData && (
                <PatientOverviewTable data={overviewData.visits} />
              )
            )}
          </div>
        ) : (
          <div>
            <Trans>Enrollment not found</Trans>
          </div>
        )}
      </div>
    );
  },
);
